import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { DriverAuthProvider } from "@/hooks/use-driver-auth";
import { OpsManagerAuthProvider } from "@/hooks/use-ops-manager-auth";
import { ProtectedDriverRoute } from "@/components/driver/ProtectedDriverRoute";
import { ThemeProvider } from "@/contexts/theme-provider";
import { CartProvider } from "@/contexts/CartContext";
import { useParams } from "react-router-dom";
import { useRequiredCompanyData } from "@/hooks/useRequiredCompanyData";
import { CompanyDataModal } from "@/components/payments/CompanyDataModal";
import { PrintA4Portal } from "@/components/PrintA4Portal";
import { ExpiredDemoGuard } from "@/components/ExpiredDemoGuard";
import "@/styles/print-invoice.css";
import { lazy, Suspense } from "react";
import { isNativePlatform } from "@/lib/platform";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AcceptInvitation from "./pages/AcceptInvitation";
import AcceptGlobalInvitation from "./pages/AcceptGlobalInvitation";
import PortalCallback from "./pages/PortalCallback";
import LicenseExpired from "./pages/LicenseExpired";

// Lazy load driver pages
const DriverAuth = lazy(() => import("./pages/driver/DriverAuth"));
const DriverPermissions = lazy(() => import("./pages/driver/DriverPermissions"));
const DriverDashboard = lazy(() => import("./pages/driver/DriverDashboard"));
const FuelCapture = lazy(() => import("./pages/driver/FuelCapture"));
const FuelHistory = lazy(() => import("./pages/driver/FuelHistory"));
const OdometerCapture = lazy(() => import("./pages/driver/OdometerCapture"));

// Lazy load ops manager pages
const SelectProfile = lazy(() => import("./pages/driver/SelectProfile"));
const OpsManagerAuth = lazy(() => import("./pages/driver/OpsManagerAuth"));
const OpsManagerDashboard = lazy(() => import("./pages/driver/OpsManagerDashboard"));
const ProtectedOpsRoute = lazy(() => import("./components/driver/ProtectedOpsRoute").then(m => ({ default: m.ProtectedOpsRoute })));


import Contact from "./pages/Contact";
import PublicPlansPage from "./pages/PublicPlansPage";
import DynamicLanding from "./pages/DynamicLanding";
import SystemDetail from "./pages/SystemDetail";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import CompaniesPage from "./pages/admin/CompaniesPage";
import UsersPage from "./pages/admin/UsersPage";
import DemoUsersPage from "./pages/admin/DemoUsersPage";
import SystemsPage from "./pages/admin/SystemsPage";
import PlansPage from "./pages/admin/PlansPage";

import { BillingAdminPage } from "./pages/admin/BillingAdminPage";
import { CompanyBillingDetailPage } from "./pages/admin/CompanyBillingDetailPage";
import SupportPage from './pages/admin/SupportPage';
import CompanySupportPage from './pages/company/SupportPage';
import ReportsPage from "./pages/admin/ReportsPage";
// SystemSettingsPage removed - settings managed via database
import NotificationsPage from "./pages/admin/NotificationsPage";
import NotificationsIntegrationPage from "./pages/admin/integrations/NotificationsPage";
import ApiPortalOnePage from "./pages/admin/integrations/ApiPortalOnePage";
import TagSystemsPage from "./pages/admin/integrations/TagSystemsPage";
import WebhookEchoPage from "./pages/admin/integrations/WebhookEchoPage";
// Payment integrations removed - migrated to Console PortalONE API
import FleetLandingPage from "./pages/fleet/LandingPage";
import TrackingOneLanding from "./pages/gps/TrackingOneLanding";
// ProductionONE removed - system discontinued
import FleetLayout from "./pages/fleet/layout";
import FleetDashboard from "./pages/fleet/Dashboard";
import FleetManagerDashboard from "./pages/fleet/FleetManagerDashboard";
import FleetOpsManagerDashboard from "./pages/fleet/FleetOpsManagerDashboard";
import FleetReports from "./pages/fleet/reports";
import FleetVehicles from "./pages/fleet/Vehicles";
import VehicleDetails from "./pages/fleet/VehicleDetails";
import FleetDrivers from "./pages/fleet/Drivers";
import FleetAssignments from "./pages/fleet/Assignments";
import FleetTrips from "./pages/fleet/Trips";
import FleetTripsAnalysis from "./pages/fleet/trips-analysis";
import FleetFuel from "./pages/fleet/Fuel";
import FleetFuelReview from "./pages/fleet/FuelReview";
import FleetMaintenance from "./pages/fleet/Maintenance";
import FleetDocuments from "./pages/fleet/Documents";
import FleetAlerts from "./pages/fleet/Alerts";
import FleetNotifications from "./pages/fleet/Notifications";
import FleetNotificationPreferences from "./pages/fleet/NotificationPreferences";
import FleetProviders from "./pages/fleet/Providers";
import FleetCosts from "./pages/fleet/Costs";
import FleetSettings from "./pages/fleet/Settings";
import FleetSupportPage from "./pages/fleet/SupportPage";
import FleetTires from "./pages/fleet/Tires";
import FleetExpenses from "./pages/fleet/Expenses";
import ExpenseCategories from "./pages/fleet/ExpenseCategories";
import { VehicleReports } from "./pages/fleet/reports/VehicleReports";
import VehicleDetailReport from "./pages/fleet/reports/VehicleDetailReport";
import { DriverReports } from "./pages/fleet/reports/DriverReports";
import { MaintenanceReports } from "./pages/fleet/reports/MaintenanceReports";
import { CostCenterReports } from "./pages/fleet/reports/CostCenterReports";
import { FinancialReports } from "./pages/fleet/reports/FinancialReports";
import { OperationalReports } from "./pages/fleet/reports/OperationalReports";
import ComparativeReports from "./pages/fleet/reports/ComparativeReports";
// ReportsDashboard removido - proibido dashboard de relatórios conforme guidelines
import ReportEditor from "./pages/fleet/settings/ReportEditor";
import DriverDetails from "./pages/fleet/DriverDetails";

// GPS imports (reutilizados pelo FleetONE)
import GpsLive from "./pages/fleet/gps/live";
import GpsDevices from "./pages/fleet/gps/devices";
import GpsCommands from "./pages/fleet/gps/commands";
import GpsNotifications from "./pages/fleet/gps/notifications";
import GpsGeofences from "./pages/fleet/gps/geofences";
import GpsAlerts from "./pages/fleet/gps/alerts";
import GpsReports from "./pages/fleet/gps/reports";
import GpsSettings from "./pages/fleet/gps/settings";
import Store from "./pages/fleet/Store";

import CompanyAdminLayout from "./pages/company/CompanyAdminLayout";
import CompanyUserLayout from "./pages/company/CompanyUserLayout";
import UserArea from "./pages/company/UserArea";

// NoteONE removed - system discontinued

// PaymentONE removed - system discontinued


import CompanyAdminDashboard from "./pages/company/CompanyAdminDashboard";
import CompanyUserDashboard from "./pages/company/CompanyUserDashboard";
import CompanyUsersPage from "./pages/company/CompanyUsersPage";
import CompanyPlansPage from "./pages/company/PlansPage";
import CompanyBillingPage from "./pages/company/BillingPage";
import CompanyReportsPage from "./pages/company/CompanyReportsPage";
import CompanySettingsPage from "./pages/company/CompanySettingsPage";
import Dashboard from "./pages/Dashboard";
import CompanySelect from "./pages/CompanySelect";
import NotFound from "./pages/NotFound";
import { UserProfile } from "./pages/UserProfile";
import { UserSettings } from "./pages/UserSettings";
import NoAccess from "./pages/NoAccess";
import ResetPassword from "./pages/ResetPassword";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
// CheckoutPage removed - payment integrations discontinued

// Create QueryClient outside component to prevent recreation on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Redirect component for fixing legacy route
function RedirectSystemUser() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/company/${slug}/user`} replace />;
}

// Company Support Route Wrapper
function CompanySupportRoute() {
  const { slug } = useParams<{ slug: string }>();
  const { companies, loading } = useAuth();
  const company = companies.find((c) => c.slug === slug);

  if (loading) return <div>Carregando...</div>;
  if (!company) return <div>Empresa não encontrada</div>;

  return <CompanySupportPage companyId={company.id} />;
}

// Component to handle required company data modal
function RequiredCompanyDataModal() {
  const {
    shouldShowModal,
    company,
    handleModalClose,
    handleModalSaved,
  } = useRequiredCompanyData();

  return (
    <CompanyDataModal
      isOpen={shouldShowModal}
      onClose={handleModalClose}
      onSaved={handleModalSaved}
      initialData={company}
      isRequired={true}
    />
  );
}

const App = () => {
  // Apply FleetONE theme on native mobile platforms
  useEffect(() => {
    if (isNativePlatform()) {
      document.documentElement.classList.add('fleet-mobile', 'dark');
      console.log('FleetONE mobile theme applied');
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="portalone-ui-theme">
        <TooltipProvider>
          <AuthProvider>
            <CartProvider>
              <RequiredCompanyDataModal />
              <PrintA4Portal />
              <Toaster richColors position="top-right" />
              <BrowserRouter>
                <ExpiredDemoGuard>
                  <Suspense fallback={<div>Carregando...</div>}>
                    <Routes>
                  {/* Driver mobile routes - wrapped in DriverAuthProvider */}
                  <Route path="/driver/select" element={
                    <OpsManagerAuthProvider>
                      <SelectProfile />
                    </OpsManagerAuthProvider>
                  } />
                  <Route path="/driver/auth" element={
                    <DriverAuthProvider>
                      <DriverAuth />
                    </DriverAuthProvider>
                  } />
                  <Route path="/driver/permissions" element={
                    <DriverAuthProvider>
                      <DriverPermissions />
                    </DriverAuthProvider>
                  } />
                  <Route path="/driver/dashboard" element={
                    <DriverAuthProvider>
                      <ProtectedDriverRoute>
                        <DriverDashboard />
                      </ProtectedDriverRoute>
                    </DriverAuthProvider>
                  } />
                  <Route path="/driver/capture" element={
                    <DriverAuthProvider>
                      <ProtectedDriverRoute>
                        <FuelCapture />
                      </ProtectedDriverRoute>
                    </DriverAuthProvider>
                  } />
                  <Route path="/driver/history" element={
                    <DriverAuthProvider>
                      <ProtectedDriverRoute>
                        <FuelHistory />
                      </ProtectedDriverRoute>
                    </DriverAuthProvider>
                  } />
                  <Route path="/driver/odometer-capture" element={
                    <DriverAuthProvider>
                      <ProtectedDriverRoute>
                        <OdometerCapture />
                      </ProtectedDriverRoute>
                    </DriverAuthProvider>
                  } />

                  {/* Ops Manager mobile routes - wrapped in OpsManagerAuthProvider */}
                  <Route path="/driver/ops-auth" element={
                    <OpsManagerAuthProvider>
                      <OpsManagerAuth />
                    </OpsManagerAuthProvider>
                  } />
                  <Route path="/driver/ops-dashboard" element={
                    <OpsManagerAuthProvider>
                      <ProtectedOpsRoute>
                        <OpsManagerDashboard />
                      </ProtectedOpsRoute>
                    </OpsManagerAuthProvider>
                  } />
                  
                  {/* Existing routes */}
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Navigate to="/" replace />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/accept-global-invitation/:token" element={<AcceptGlobalInvitation />} />
            <Route path="/auth/portal-callback" element={<PortalCallback />} />
            
            <Route path="/contact" element={<Contact />} />
            <Route path="/plans" element={<PublicPlansPage />} />
            <Route path="/systems/:code" element={<SystemDetail />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/company/select" element={<CompanySelect />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/no-access" element={<NoAccess />} />
            <Route path="/license-expired" element={<LicenseExpired />} />
            
            {/* User profile and settings routes */}
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/fleet" element={<Navigate to="/" replace />} />
            {/* ProductionONE removed - system discontinued */}
            <Route path="/app/fleet" element={<FleetLayout />}>
              <Route index element={<FleetDashboard />} />
              <Route path="vehicles" element={<FleetVehicles />} />
              <Route path="drivers" element={<FleetDrivers />} />
              <Route path="assignments" element={<FleetAssignments />} />
              <Route path="trips" element={<FleetTrips />} />
              <Route path="fuel" element={<FleetFuel />} />
              <Route path="maintenance" element={<FleetMaintenance />} />
              <Route path="tires" element={<FleetTires />} />
              <Route path="documents" element={<FleetDocuments />} />
              <Route path="alerts" element={<FleetAlerts />} />
              <Route path="providers" element={<FleetProviders />} />
              <Route path="costs" element={<FleetCosts />} />
              <Route path="expenses" element={<FleetExpenses />} />
              <Route path="settings" element={<FleetSettings />} />
              <Route path="settings/reports" element={<ReportEditor />} />
              <Route path="reports" element={<FleetReports />} />
            </Route>
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="demo-users" element={<DemoUsersPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="plans" element={<PlansPage />} />
              <Route path="payments" element={<BillingAdminPage />} />
              <Route path="billing" element={<BillingAdminPage />} />
              <Route path="billing/:companyId" element={<CompanyBillingDetailPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="systems" element={<SystemsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              {/* SystemSettingsPage removed - settings managed via database */}
              {/* Payment gateways integration removed */}
              <Route path="integrations/notifications" element={<NotificationsIntegrationPage />} />
              <Route path="integrations/api-portalone" element={<ApiPortalOnePage />} />
              <Route path="integrations/tag-systems" element={<TagSystemsPage />} />
              <Route path="integrations/echo" element={<WebhookEchoPage />} />
              {/* DevOpsONE removed - system discontinued */}
              {/* Leads & CRM removed - system discontinued */}
              
              {/* PaymentONE removed - system discontinued */}

              {/* NoteONE removed - system discontinued */}
            </Route>
            
            {/* BTG removed - migrated to Console PortalONE API */}
            {/* New User Area Routes */}
            <Route path="/company/:slug/user" element={<CompanyUserLayout />}>
              <Route index element={<UserArea />} />
              <Route path="dashboard" element={<UserArea />} />
              <Route path="profile" element={<UserProfile />} />
              <Route path="settings" element={<UserSettings />} />
              
              {/* Systems within User Area */}
              <Route path="systems/fleet" element={<FleetLayout />}>
                <Route index element={<FleetDashboard />} />
                <Route path="manager" element={<FleetManagerDashboard />} />
                <Route path="vehicles" element={<FleetVehicles />} />
                <Route path="equipment" element={<GpsDevices />} />
                <Route path="drivers" element={<FleetDrivers />} />
                <Route path="assignments" element={<FleetAssignments />} />
                <Route path="trips" element={<FleetTrips />} />
                <Route path="fuel" element={<FleetFuel />} />
                <Route path="maintenance" element={<FleetMaintenance />} />
                <Route path="tires" element={<FleetTires />} />
                <Route path="documents" element={<FleetDocuments />} />
                <Route path="alerts" element={<FleetAlerts />} />
                <Route path="providers" element={<FleetProviders />} />
                <Route path="costs" element={<FleetCosts />} />
                <Route path="expenses" element={<FleetExpenses />} />
                <Route path="reports" element={<FleetReports />} />
                <Route path="settings" element={<FleetSettings />} />
                <Route path="support" element={<FleetSupportPage />} />
                {/* GPS Monitoring Routes - COMPLETE */}
                <Route path="monitoring/live" element={<GpsLive />} />
                <Route path="monitoring/geofences" element={<GpsGeofences />} />
                <Route path="monitoring/alerts" element={<GpsAlerts />} />
                <Route path="monitoring/commands" element={<GpsCommands />} />
                
                {/* GPS Store */}
                <Route path="store" element={<Store />} />
                
                {/* GPS Equipment */}
                <Route path="equipment" element={<GpsDevices />} />
                
                {/* GPS Settings */}
                <Route path="settings" element={<FleetSettings />} />
                <Route path="settings/gps" element={<GpsSettings />} />
                
                {/* GPS Additional Pages */}
                <Route path="notifications" element={<GpsNotifications />} />
                <Route path="reports/gps" element={<GpsReports />} />
                
            </Route>
            
            {/* Redirect legacy TrackingONE to FleetONE - User Area */}
            <Route 
              path="systems/gps_tracking/*" 
              element={<Navigate to="../systems/fleet/monitoring/live" replace />} 
            />
              
              {/* NoteONE removed - system discontinued */}
              
              {/* ProductionONE removed - system discontinued */}
              
              {/* Admin routes within User Area - Legacy support */}
              <Route path="admin" element={<CompanyAdminDashboard />} />
              <Route path="admin/support" element={<CompanySupportRoute />} />
              <Route path="admin/users" element={<CompanyUsersPage />} />
              <Route path="admin/plans" element={<CompanyPlansPage />} />
              <Route path="admin/billing" element={<CompanyBillingPage />} />
              <Route path="admin/reports" element={<CompanyReportsPage />} />
              <Route path="admin/settings" element={<CompanySettingsPage />} />
            </Route>

            {/* Redirect for legacy system/user route */}
            <Route path="/company/:slug/system/user" element={<RedirectSystemUser />} />

            {/* Legacy Admin Layout (backwards compatibility) */}
            <Route path="/company/:slug/admin" element={<CompanyAdminLayout />}>
              <Route index element={<CompanyAdminDashboard />} />
              <Route path="support" element={<CompanySupportRoute />} />
              <Route path="users" element={<CompanyUsersPage />} />
              <Route path="plans" element={<CompanyPlansPage />} />
              <Route path="billing" element={<CompanyBillingPage />} />
              <Route path="reports" element={<CompanyReportsPage />} />
              <Route path="settings" element={<CompanySettingsPage />} />
              {/* Checkout removed - payment integrations discontinued */}
              
              {/* System routes within admin */}
              <Route path="systems/fleet" element={<FleetDashboard />} />
              <Route path="systems/fleet/manager" element={<FleetManagerDashboard />} />
              <Route path="systems/fleet/ops" element={<FleetOpsManagerDashboard />} />
              <Route path="systems/fleet/vehicles" element={<FleetVehicles />} />
              <Route path="systems/fleet/equipment" element={<GpsDevices />} />
              <Route path="systems/fleet/vehicles/:vehicleId" element={<VehicleDetails />} />
              <Route path="systems/fleet/drivers" element={<FleetDrivers />} />
              <Route path="systems/fleet/drivers/:driverId" element={<DriverDetails />} />
              <Route path="systems/fleet/assignments" element={<FleetAssignments />} />
              <Route path="systems/fleet/trips" element={<FleetTrips />} />
              <Route path="systems/fleet/trips-analysis" element={<FleetTripsAnalysis />} />
              <Route path="systems/fleet/fuel" element={<FleetFuel />} />
              <Route path="systems/fleet/fuel-review" element={<FleetFuelReview />} />
              <Route path="systems/fleet/maintenance" element={<FleetMaintenance />} />
              <Route path="systems/fleet/tires" element={<FleetTires />} />
              <Route path="systems/fleet/documents" element={<FleetDocuments />} />
              <Route path="systems/fleet/alerts" element={<FleetAlerts />} />
              <Route path="systems/fleet/notifications" element={<FleetNotifications />} />
              <Route path="systems/fleet/notifications/preferences" element={<FleetNotificationPreferences />} />
              <Route path="systems/fleet/providers" element={<FleetProviders />} />
              <Route path="systems/fleet/costs" element={<FleetCosts />} />
              <Route path="systems/fleet/expenses" element={<FleetExpenses />} />
              <Route path="systems/fleet/expense-categories" element={<ExpenseCategories />} />
              <Route path="systems/fleet/reports" element={<FleetReports />} />
              <Route path="systems/fleet/settings" element={<FleetSettings />} />
              <Route path="systems/fleet/support" element={<FleetSupportPage />} />
              {/* GPS Monitoring Routes - COMPLETE */}
              <Route path="systems/fleet/monitoring/live" element={<GpsLive />} />
              <Route path="systems/fleet/monitoring/geofences" element={<GpsGeofences />} />
              <Route path="systems/fleet/monitoring/alerts" element={<GpsAlerts />} />
              <Route path="systems/fleet/monitoring/commands" element={<GpsCommands />} />
              
              {/* GPS Store */}
              <Route path="systems/fleet/store" element={<Store />} />
              
              {/* GPS Equipment */}
              <Route path="systems/fleet/equipment" element={<GpsDevices />} />
              
              {/* GPS Settings */}
              <Route path="systems/fleet/settings" element={<FleetSettings />} />
              <Route path="systems/fleet/settings/reports" element={<ReportEditor />} />
              <Route path="systems/fleet/settings/gps" element={<GpsSettings />} />
              
              {/* GPS Additional Pages */}
              <Route path="systems/fleet/notifications" element={<GpsNotifications />} />
              <Route path="systems/fleet/reports/gps" element={<GpsReports />} />
              {/* NoteONE removed - system discontinued */}
              
              {/* ProductionONE removed - system discontinued */}
              
              {/* Redirect legacy TrackingONE to FleetONE - Admin Area */}
              <Route 
                path="systems/gps_tracking/*" 
                element={<Navigate to="../systems/fleet/monitoring/live" replace />} 
              />

              {/* PaymentONE removed - system discontinued */}
            </Route>
            {/* Redirect legacy /company/:slug/systems/* to admin context */}
            <Route 
              path="/company/:slug/systems/*" 
              element={<Navigate to={location.pathname.replace('/systems/', '/admin/systems/')} replace />} 
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            
            {/* TrackingONE Landing Page */}
            <Route path="/trackingone" element={<TrackingOneLanding />} />
            
            {/* Dynamic Landing Pages Route - MUST be near the end to avoid conflicts */}
            <Route path="/:systemCode" element={<DynamicLanding />} />
            
            <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ExpiredDemoGuard>
              </BrowserRouter>
            </CartProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
